'use client'
import { useState, type KeyboardEvent } from 'react'
import Image from 'next/image'
import styles from './UrlAnalysis.module.scss'
import Footer from '@/src/components/commons/Footer/Footer'
import { isValidUrl } from '@/src/utils/common'
import TickIcon from '@/public/assets/images/green_check_icon.svg'
import ScoreBar from '@/public/assets/images/scoreBar.png'
import Skeleton from '@/src/components/commons/generics/Skeleton/Skeleton'
import ClearableTextInput from '@/src/components/commons/generics/ClearableTextInput/ClearableTextInput'

const UrlAnalysis = () => {
  const [selected, setSelected] = useState('https://')
  const [value, setValue] = useState('')
  const [loading, setLoading] = useState(false)
  const [finalInfo, setFinalInfo] = useState({
    url: '',
    date: '',
    score: 0,
  })
  const [error, setError] = useState(false)

  const showError = value !== '' && !isValidUrl(value)

  const getUrl = (value) => {
    let modifiedValue = value
    if (!modifiedValue.startsWith('www.')) {
      if (modifiedValue.startsWith('http://') || modifiedValue.startsWith('https://')) {
        const [scheme, rest] = modifiedValue.split('://')
        if (!rest.startsWith('www.')) {
          modifiedValue = `${scheme}://www.${rest}`
        }
      } else {
        modifiedValue = `www.${modifiedValue}`
      }
    }
    return modifiedValue
  }

  const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

  const requestScan = async (urlToScan: string, options?: { useCache?: boolean }) => {
    const params = new URLSearchParams({ url: urlToScan })
    if (options?.useCache) params.set('cache', '1')
    // nosemgrep: nodejs_scan.javascript-ssrf-rule-node_ssrf -- same-origin BFF; server validates URL and only fetches allowlisted Zulu origin.
    const response = await fetch(`/api/scan?${params.toString()}`, { method: 'POST' })
    const data = await response.json()
    if (!response.ok || data?.status !== 'ok' || !data?.id) return null
    return data
  }

  const requestReport = async (id: string) => {
    const response = await fetch(`/api/report?id=${encodeURIComponent(id)}`, { method: 'POST' })
    const data = await response.json()
    if (!response.ok) return null
    return data
  }

  const pollReportUntilReady = async (id: string) => {
    const maxAttempts = 30
    const intervalMs = 2000
    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      const report = await requestReport(id)
      if (!report) return null
      if (report.status === 'finished') return report
      if (report.status !== 'queued') return null
      await sleep(intervalMs)
    }
    return null
  }

  const callAnalyzer = async ({ rescan = false }: { rescan?: boolean } = {}) => {
    const trimmed = value.trim()
    const urlToScan = trimmed ? `${selected}${getUrl(trimmed)}` : finalInfo.url
    if (!urlToScan) return

    try {
      setFinalInfo({ url: '', date: '', score: 0 })
      setError(false)
      setLoading(true)

      let scanData = null
      if (rescan) {
        scanData = await requestScan(urlToScan)
      } else {
        scanData = await requestScan(urlToScan, { useCache: true })
        if (!scanData) {
          scanData = await requestScan(urlToScan)
        }
      }
      if (!scanData?.id) {
        setError(true)
        return
      }

      const reqInfo = await pollReportUntilReady(String(scanData.id))
      if (!reqInfo?.url || !reqInfo?.date || reqInfo.score == null) {
        setError(true)
        return
      }

      setFinalInfo({
        url: reqInfo.url,
        date: formatDateString(reqInfo.date),
        score: reqInfo.score,
      })
    } catch (error) {
      setError(true)
      console.log(error)
    } finally {
      setLoading(false)
      setValue('')
    }
  }

  const handleRescan = () => {
    void callAnalyzer({ rescan: true })
  }

  const handleRescanKeyDown = (e: KeyboardEvent<HTMLParagraphElement>) => {
    if (e.key !== 'Enter' && e.key !== ' ') {
      return
    }
    e.preventDefault()
    void callAnalyzer({ rescan: true })
  }

  const formatDateString = (input) => {
    const [date, time] = input.split(' ')
    const [fullTime, micro] = time.split('.')
    const ms = micro ? micro.slice(0, 3) : '000'
    const isoString = `${date}T${fullTime}.${ms}Z`
    const dateObj = new Date(isoString)
    return dateObj
      .toLocaleString('en-US', {
        month: 'short',
        day: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
        timeZone: 'UTC',
        timeZoneName: 'short',
      })
      .replace('UTC', 'UTC')
  }

  return (
    <>
      <div className={`${styles?.urlContainer} flex justify-center`}>
        <div
          className={`${styles?.urlInnerCont} px-[16px] lg:px-0 py-[70px] w-full flex flex-col justify-start`}
        >
          <p className={`${styles.heading} font-dm-mono mb-[20px]`}>{'URL Risk Analyzer'}</p>
          <div className={`${styles.description} font-gt-haptik flex flex-col`}>
            <p>{'How safe is your web destination?'}</p>
            <p>{'Zulu is a dynamic risk scoring engine for web based content.'}</p>
          </div>

          <div className={`${styles.line} my-[40px]`}></div>

          <form
            className="flex flex-col gap-[30px]"
            onSubmit={(e) => {
              e.preventDefault()
              if (!showError && value.trim() !== '' && !loading) void callAnalyzer()
            }}
          >
            <div className={`${styles.radioGroup} font-dm-mono`}>
              <label className={styles.radioLabel}>
                <input
                  type="radio"
                  name="options"
                  value="https://"
                  checked={selected === 'https://'}
                  onChange={() => setSelected('https://')}
                />
                <span>https</span>
              </label>
              <label className={styles.radioLabel}>
                <input
                  type="radio"
                  name="options"
                  value="http://"
                  checked={selected === 'http://'}
                  onChange={() => setSelected('http://')}
                />
                <span>http</span>
              </label>
            </div>
            <div className={`${styles.inputWrapper} font-gt-haptik`}>
              {showError && (
                <p className={`font-dm-mono ${styles.validatorText}`}>Enter Valid URL</p>
              )}
              <ClearableTextInput
                value={value}
                onChange={setValue}
                placeholder="E.g. google.com"
                inputClassName={styles.inputField}
                containerClassName={styles.clearableInputContainer}
                clearButtonAriaLabel="Clear URL"
                autoFocus
              />
              <p className={`font-dm-mono ${styles.warningText}`}>
                * Add url without www or https or http
              </p>
            </div>
            <div className="font-dm-mono">
              <button
                type="submit"
                className={`${styles.searchBtn} cursor-pointer`}
                disabled={showError || value === '' || loading}
              >
                {loading ? 'Loading...' : 'Search'}
              </button>
            </div>
          </form>

          {loading && (
            <div className="my-[50px]">
              <Skeleton height="1.5rem" count={3} />
            </div>
          )}

          {finalInfo?.url && !loading && (
            <div className="my-[50px] flex flex-col gap-[20px]">
              <p className={`${styles.resultTitle} font-dm-mono`}>URL information</p>
              <div className="flex w-full">
                <div
                  className={`${styles.urlInfo} p-[20px] flex flex-col gap-[10px] font-gt-haptik`}
                >
                  <p className="flex gap-[10px]">
                    <span className={styles.sideLabel}>URL:</span>
                    <span className={styles.url}>{finalInfo?.url}</span>
                  </p>
                  <p className="flex gap-[10px]">
                    <span className={styles.sideLabel}>Performed on:</span>
                    <span className={styles.date}>{finalInfo?.date}</span>
                  </p>
                  <p className="flex gap-[10px]">
                    <span className={styles.sideLabel}>Status:</span>
                    <div className="flex items-center gap-[5px]">
                      <Image src={TickIcon} alt="" />
                      <span className={styles.status}>Completed</span>
                    </div>
                  </p>
                </div>
                <div
                  className={`${styles.resultInfo} p-[20px] flex flex-col gap-[10px] items-end`}
                >
                  <p className={styles.resultHeading}>test results</p>
                  <div className={`flex gap-[10px] ${styles.res}`}>
                    <div className={`flex gap-[5px]`}>
                      <Image src={TickIcon} alt="" />
                      <span className={styles.green}>Benign</span>
                    </div>
                    <div className="relative">
                      <Image src={ScoreBar} alt="" />
                      <span
                        className="absolute bottom-[2px] text-[14px]"
                        style={{
                          left: finalInfo?.score === 0 ? '-3px' : `${finalInfo?.score}px`,
                        }}
                      >
                        |
                      </span>
                    </div>
                    <div>{finalInfo?.score}/100</div>
                  </div>
                  <p
                    className={`${styles.rescan} font-dm-mono cursor-pointer`}
                    role="button"
                    tabIndex={0}
                    aria-label="Rescan URL"
                    onClick={handleRescan}
                    onKeyDown={handleRescanKeyDown}
                  >
                    Rescan
                  </p>
                </div>
              </div>
            </div>
          )}

          {error && (
            <p className={`mt-[50px] ${styles.description} font-gt-haptik`}>
              Error: Unable to load data
            </p>
          )}
        </div>
      </div>
      <Footer />
    </>
  )
}

export default UrlAnalysis
